import React, { useEffect } from 'react';
import { ArrowRight, TrendingUp, Users, Shield, DollarSign } from 'lucide-react';

function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    els.forEach((el) => el.classList.remove('visible'));
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.08, rootMargin: '0px 0px -20px 0px' }
    );
    const timer = setTimeout(() => els.forEach((el) => observer.observe(el)), 50);
    return () => { clearTimeout(timer); observer.disconnect(); };
  }, []);
}

const steps = [
  { step: '01', title: 'Submit Enquiry', desc: 'Fill out our franchise enquiry form with your details and preferred location.' },
  { step: '02', title: 'Initial Discussion', desc: 'Our franchise team will contact you within 48 hours for an initial discussion.' },
  { step: '03', title: 'Site Evaluation', desc: 'Our team visits and evaluates your proposed store location for viability.' },
  { step: '04', title: 'Agreement Signing', desc: 'Sign the franchise agreement and complete the onboarding formalities.' },
  { step: '05', title: 'Store Setup', desc: 'We assist with store design, fit-out, technology setup, and stock procurement.' },
  { step: '06', title: 'Grand Opening', desc: 'Launch your APlus Pharmacy outlet with our full marketing and operational support.' },
];

const Franchise = () => {
  useScrollReveal();
  return (
    <div className="">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white py-28 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:60px_60px]"></div>
        <div className="absolute top-10 right-1/4 w-96 h-96 bg-secondary-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-10 w-72 h-72 bg-primary-400/20 rounded-full blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-semibold mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
            Franchise Opportunities
            <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 inline-block"></span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold leading-tight mb-5">
            Build Your Business with
            <br /><span className="text-secondary-400">APlus Pharmacy</span>
          </h1>
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-12 bg-white/30 rounded-full"></span>
            <span className="w-2 h-2 rounded-full bg-secondary-400"></span>
            <span className="h-px w-12 bg-white/30 rounded-full"></span>
          </div>
          <p className="text-white/75 text-base lg:text-lg max-w-3xl mx-auto leading-relaxed mb-8">Join India's fastest-growing pharmacy retail chain. Own an APlus Pharmacy franchise and enjoy a proven business model, strong brand, and comprehensive support system.</p>
          <a href="#enquiry" className="btn-secondary">Apply for Franchise <ArrowRight className="w-4 h-4" /></a>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '₹15-25 Lakh', label: 'Investment Range' },
              { value: '18-24 Months', label: 'ROI Period' },
              { value: '25-35%', label: 'Gross Margin' },
              { value: '500+', label: 'Target Outlets by 2027' },
            ].map((s, i) => (
              <div key={i} className="reveal">
                <div className="font-heading text-3xl font-bold text-primary-600 mb-1">{s.value}</div>
                <div className="text-gray-600 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              Why Partner With Us
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">Why Choose</span>
              <span className="gradient-text">APlus Franchise?</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500 max-w-2xl mx-auto">Everything you need to build a thriving pharmacy business — backed by APlus Healthcare.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Proven Business Model', desc: 'Leverage our tested pharmacy retail model with established SOPs and best practices.', color: 'text-primary-600', bg: 'bg-primary-50' },
              { icon: TrendingUp, title: 'Growing Industry', desc: 'India\'s pharmacy retail market is growing at 15% CAGR — massive opportunity ahead.', color: 'text-secondary-600', bg: 'bg-secondary-50' },
              { icon: Users, title: 'Full Support System', desc: 'Training, marketing, supply chain, technology, and operational support from day one.', color: 'text-amber-600', bg: 'bg-amber-50' },
              { icon: DollarSign, title: 'Attractive Returns', desc: 'Gross margins of 25-35% with breakeven typically within 18-24 months.', color: 'text-rose-600', bg: 'bg-rose-50' },
              { icon: Shield, title: 'Strong Brand', desc: 'Operate under the trusted APlus Pharmacy brand with established customer loyalty.', color: 'text-violet-600', bg: 'bg-violet-50' },
              { icon: TrendingUp, title: 'Technology Enabled', desc: 'Complete POS, inventory, billing, and reporting software provided.', color: 'text-blue-600', bg: 'bg-blue-50' },
            ].map((item, i) => (
              <div key={i} className="reveal card">
                <div className={`w-14 h-14 ${item.bg} rounded-2xl flex items-center justify-center mb-5`}>
                  <item.icon className={`w-7 h-7 ${item.color}`} />
                </div>
                <h3 className="font-heading font-bold text-gray-900 text-lg mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment & Steps */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
              Investment &amp; Process
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">Investment &amp; Getting</span>
              <span className="gradient-text">Started</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-secondary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-primary-400 rounded-full"></span>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="reveal stagger-1">
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-6">Franchise <span className="gradient-text">Investment Details</span></h3>
              <div className="space-y-4">
                {[
                  { item: 'Franchise Fee', cost: '₹3-5 Lakh', note: 'One-time fee' },
                  { item: 'Store Setup & Fit-out', cost: '₹5-8 Lakh', note: 'Includes branding, interiors, fixtures' },
                  { item: 'Initial Stock', cost: '₹5-10 Lakh', note: 'Opening inventory' },
                  { item: 'Technology & Software', cost: '₹1-2 Lakh', note: 'POS, billing, inventory system' },
                  { item: 'Working Capital', cost: '₹2-3 Lakh', note: 'For first 3 months operations' },
                  { item: 'Total Investment', cost: '₹15-25 Lakh', note: 'Approximate total', highlight: true },
                ].map((row, i) => (
                  <div key={i} className={`flex items-center justify-between p-4 rounded-xl ${row.highlight ? 'bg-primary-600 text-white' : 'bg-gray-50 border border-gray-100'}`}>
                    <div>
                      <div className={`font-semibold ${row.highlight ? 'text-white' : 'text-gray-900'}`}>{row.item}</div>
                      <div className={`text-sm ${row.highlight ? 'text-white/80' : 'text-gray-500'}`}>{row.note}</div>
                    </div>
                    <div className={`font-heading font-bold text-lg ${row.highlight ? 'text-white' : 'text-primary-600'}`}>{row.cost}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal stagger-2">
              <h3 className="font-heading text-2xl font-bold text-gray-900 mb-6">How to <span className="gradient-text">Get Started</span></h3>
              <div className="space-y-6">
                {steps.map((s, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 bg-primary-600 text-white rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0">{s.step}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{s.title}</h3>
                      <p className="text-gray-600 text-sm">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="enquiry" className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="reveal text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 rounded-full px-4 py-2 text-sm font-semibold mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
              Apply Now
              <span className="w-1.5 h-1.5 rounded-full bg-primary-600 inline-block"></span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              <span className="block mb-2">Franchise</span>
              <span className="gradient-text">Enquiry Form</span>
            </h2>
            <div className="flex items-center justify-center gap-3 mt-3 mb-4">
              <span className="h-px w-12 bg-gradient-to-r from-transparent to-primary-400 rounded-full"></span>
              <span className="w-2 h-2 rounded-full bg-secondary-500"></span>
              <span className="h-px w-12 bg-gradient-to-l from-transparent to-secondary-400 rounded-full"></span>
            </div>
            <p className="text-base text-gray-500">Fill in your details and our team will reach out within 48 hours.</p>
          </div>
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { label: 'Full Name', type: 'text', placeholder: 'Your full name' },
                { label: 'Phone Number', type: 'tel', placeholder: '+91 XXXXX XXXXX' },
                { label: 'Email Address', type: 'email', placeholder: 'your@email.com' },
                { label: 'City / Location', type: 'text', placeholder: 'Preferred city for outlet' },
              ].map((field, i) => (
                <div key={i}>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{field.label}</label>
                  <input type={field.type} placeholder={field.placeholder} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all" />
                </div>
              ))}
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Investment Capacity</label>
                <select className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all">
                  <option>Select your investment range</option>
                  <option>₹15-20 Lakh</option>
                  <option>₹20-25 Lakh</option>
                  <option>₹25-30 Lakh</option>
                  <option>Above ₹30 Lakh</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
                <textarea rows={4} placeholder="Tell us about your background and why you want to become an APlus Pharmacy franchisee..." className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"></textarea>
              </div>
              <div className="md:col-span-2">
                <button className="btn-primary w-full justify-center text-base py-4">Submit Franchise Enquiry <ArrowRight className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Franchise;
